import { useState } from 'react';
import { ChevronDown, ChevronRight, Eye, EyeOff, GripVertical, X } from 'lucide-react';
import { fmt } from '@cca/ui';
import type { DragKind, DragSystem } from '../lib/dragSystem';
import type { ProjectedLine, CpiDefaults } from '../lib/projection';
import type { LineItem, Project } from '../types';
import { TombstoneIcon } from './TombstoneIcon';
import { useIsViewer } from '../lib/viewerContext';

type Props = {
  row: ProjectedLine;
  lineNo: number;
  defaults: CpiDefaults;
  projects: Project[];
  isHidden: boolean;
  kind: DragKind;
  dragSystem: DragSystem;
  /** Prior-year actual from the imported Xero P&L. null = no match. */
  actual: number | null;
  /** Y1 is owned by the Quota Calculator on this row — read-only.
   *  Y2 and Y3 default to the calculator's value but the user can
   *  override them; clearing the override reverts to the calculator
   *  number (held in `quotaY*` on the row). */
  lockY1?: boolean;
  /** Row was added in this session (not yet on the Sheet). Swaps the
   *  archive/tombstone delete for a hard remove — fresh mistakes
   *  shouldn't end up in the Archive panel. */
  isUnsaved?: boolean;
  onUpdate: (id: string, patch: Partial<LineItem>) => void;
  onDelete: (id: string) => void;
  /** Hard-delete callback used when isUnsaved is true. */
  onRemove?: (id: string) => void;
  onToggleHidden: (id: string) => void;
  onAssignProject: (id: string, projectId: string | null) => void;
};

// Per-line CPI override: stored as `null` (use default) or a number
// (including an explicit 0 = pass-through). Empty input → null.
const parseOverride = (raw: string): number | null => {
  if (raw.trim() === '') return null;
  const n = parseFloat(raw);
  return Number.isNaN(n) ? null : n;
};

/** Whether a row carries content the user explicitly typed: a real
 *  note, a non-zero CPI override (0 = pass-through, doesn't count),
 *  or a manual amount override on any forecast year. Drives the gold
 *  dot behind the row chevron, and the same dot on collapsed section
 *  chevrons when any contained row qualifies. */
export function hasLineCustomisation(line: LineItem): boolean {
  const hasNote = line.note.trim().length > 0;
  const hasCpi = (line.cpiY1 ?? 0) !== 0 || (line.cpiY2 ?? 0) !== 0 || (line.cpiY3 ?? 0) !== 0;
  const hasOverride = line.overrideY1 != null || line.overrideY2 != null || line.overrideY3 != null;
  return hasNote || hasCpi || hasOverride;
}

export function BudgetRow({
  row,
  lineNo,
  defaults,
  projects,
  isHidden,
  kind,
  dragSystem,
  actual,
  lockY1,
  isUnsaved,
  onUpdate,
  onDelete,
  onRemove,
  onToggleHidden,
  onAssignProject,
}: Props) {
  const isViewer = useIsViewer();
  const rowDrag = isViewer ? {} : dragSystem.rowHandlers(kind, row.id);
  const isDragging = !isViewer && dragSystem.isDragging(row.id);
  const dropPos = isViewer ? null : dragSystem.rowDropPosition(row.id);
  const [expanded, setExpanded] = useState(false);

  const hasCustomisation = hasLineCustomisation(row);

  const customisationDetail = hasCustomisation
    ? [
        row.overrideY1 != null ? 'FY2027 override' : null,
        row.overrideY2 != null ? 'FY2028 override' : null,
        row.overrideY3 != null ? 'FY2029 override' : null,
        (row.cpiY1 ?? 0) !== 0 ? 'Y1 CPI' : null,
        (row.cpiY2 ?? 0) !== 0 ? 'Y2 CPI' : null,
        (row.cpiY3 ?? 0) !== 0 ? 'Y3 CPI' : null,
        row.note.trim().length > 0 ? 'note' : null,
      ]
        .filter(Boolean)
        .join(', ')
    : '';

  return (
    <>
      <tr
        className={`row-data ${isHidden ? 'is-hidden' : ''} ${expanded ? 'is-expanded' : ''} ${
          isDragging ? 'is-dragging' : ''
        } ${dropPos === 'before' ? 'drop-before' : ''} ${dropPos === 'after' ? 'drop-after' : ''}`}
        {...rowDrag}
      >
        <td className="text-center text-neutral-300 drag-handle">
          {!isViewer && <GripVertical className="w-3 h-3 inline opacity-30" />}
        </td>
        <td className="no-print">
          <div className="flex items-center justify-center">
            <button
              type="button"
              className={`row-control ${hasCustomisation ? 'has-detail' : ''}`}
              onClick={() => setExpanded((e) => !e)}
              title={
                hasCustomisation
                  ? `Has: ${customisationDetail} — click to ${expanded ? 'collapse' : 'view'}`
                  : expanded
                    ? 'Collapse details'
                    : 'Expand details (CPI overrides · project · notes)'
              }
              aria-label="Toggle details"
            >
              {expanded ? (
                <ChevronDown className="w-4 h-4" />
              ) : (
                <ChevronRight className="w-4 h-4" />
              )}
            </button>
          </div>
        </td>
        <td className="text-center line-no">{lineNo}</td>
        <td>
          <span className="line-code">{row.code || '—'}</span>
        </td>
        <td className="col-name">
          <span className="line-name">{row.name}</span>
        </td>
        <td className="col-actual text-right num">
          {actual === null ? <span className="text-ccaanz-grey">—</span> : fmt(actual)}
        </td>
        <td className="col-current text-right">
          <CurrentAmountInput
            value={row.currentAmount}
            readOnly={isViewer}
            onCommit={(n) => onUpdate(row.id, { currentAmount: n })}
          />
          <span className="print-only num">{fmt(row.currentAmount)}</span>
        </td>
        <td className="col-shade text-right">
          <ForecastInput
            displayedValue={row.y1}
            override={row.overrideY1}
            readOnly={lockY1 || isViewer}
            readOnlyTitle={
              isViewer
                ? 'Read-only — generate a registrar account to edit'
                : 'Calculated by the Quota Calculator — adjust on the Quota page'
            }
            onCommit={(n) => onUpdate(row.id, { overrideY1: n })}
          />
          <span className="print-only num">{fmt(row.y1)}</span>
        </td>
        <td className="text-right">
          <ForecastInput
            displayedValue={row.y2}
            override={row.overrideY2}
            readOnly={isViewer}
            readOnlyTitle="Read-only"
            onCommit={(n) => onUpdate(row.id, { overrideY2: n })}
          />
          <span className="print-only num">{fmt(row.y2)}</span>
        </td>
        <td className="col-shade text-right">
          <ForecastInput
            displayedValue={row.y3}
            override={row.overrideY3}
            readOnly={isViewer}
            readOnlyTitle="Read-only"
            onCommit={(n) => onUpdate(row.id, { overrideY3: n })}
          />
          <span className="print-only num">{fmt(row.y3)}</span>
        </td>
        <td className="no-print">
          <div className="flex items-center justify-end gap-0.5">
            {!isViewer && (
              <button
                type="button"
                className="row-control"
                onClick={() => onToggleHidden(row.id)}
                disabled={Math.abs(row.currentAmount) >= 0.01 && !isHidden}
                title={
                  Math.abs(row.currentAmount) < 0.01
                    ? isHidden
                      ? 'Show this row'
                      : 'Hide this zero-value row'
                    : 'Only zero-value rows can be hidden'
                }
                aria-label={isHidden ? 'Show row' : 'Hide row'}
              >
                {isHidden ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            )}
            {!isViewer &&
              (isUnsaved && onRemove ? (
                <button
                  type="button"
                  className="row-control danger"
                  onClick={() => onRemove(row.id)}
                  title="Remove this newly-added line (not saved yet — gone for good)"
                  aria-label="Remove unsaved line"
                >
                  <X className="w-4 h-4" />
                </button>
              ) : (
                <button
                  type="button"
                  className="row-control danger"
                  onClick={() => onDelete(row.id)}
                  title="Archive this line (soft delete — restorable from Archive panel)"
                  aria-label="Archive line"
                >
                  <TombstoneIcon className="w-4 h-4" />
                </button>
              ))}
          </div>
        </td>
      </tr>

      {row.note.trim().length > 0 && (
        <tr className="row-print-note">
          <td colSpan={11}>
            <span className="note-prefix">Note:</span>
            {row.note}
          </td>
        </tr>
      )}

      {expanded && (
        <tr className="row-detail">
          <td colSpan={11}>
            <div className="detail-shell">
              <div className="detail-cpi">
                <div className="detail-label">Y1 CPI</div>
                <div className="pct-input pct-block">
                  <input
                    type="number"
                    step={0.1}
                    className={`input-cpi ${row.cpiY1 != null ? 'input-cpi--set' : ''}`}
                    value={row.cpiY1 ?? ''}
                    placeholder={defaults.cpiY1.toString()}
                    disabled={isViewer}
                    onChange={(e) => onUpdate(row.id, { cpiY1: parseOverride(e.target.value) })}
                    aria-label="Y1 CPI override"
                  />
                  <span className="pct-suffix">%</span>
                </div>
              </div>
              <div className="detail-cpi">
                <div className="detail-label">Y2 CPI</div>
                <div className="pct-input pct-block">
                  <input
                    type="number"
                    step={0.1}
                    className={`input-cpi ${row.cpiY2 != null ? 'input-cpi--set' : ''}`}
                    value={row.cpiY2 ?? ''}
                    placeholder={defaults.cpiY2.toString()}
                    disabled={isViewer}
                    onChange={(e) => onUpdate(row.id, { cpiY2: parseOverride(e.target.value) })}
                    aria-label="Y2 CPI override"
                  />
                  <span className="pct-suffix">%</span>
                </div>
              </div>
              <div className="detail-cpi">
                <div className="detail-label">Y3 CPI</div>
                <div className="pct-input pct-block">
                  <input
                    type="number"
                    step={0.1}
                    className={`input-cpi ${row.cpiY3 != null ? 'input-cpi--set' : ''}`}
                    value={row.cpiY3 ?? ''}
                    placeholder={defaults.cpiY3.toString()}
                    disabled={isViewer}
                    onChange={(e) => onUpdate(row.id, { cpiY3: parseOverride(e.target.value) })}
                    aria-label="Y3 CPI override"
                  />
                  <span className="pct-suffix">%</span>
                </div>
              </div>

              <div className="detail-project">
                <div className="detail-label">Project</div>
                <select
                  className={`project-select ${row.projectId ? 'is-projected' : ''}`}
                  value={row.projectId ?? ''}
                  disabled={isViewer}
                  onChange={(e) => onAssignProject(row.id, e.target.value || null)}
                >
                  <option value="">— operating —</option>
                  {projects.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="detail-notes">
                <div className="detail-label">Notes</div>
                <input
                  type="text"
                  className="input-note"
                  value={row.note}
                  readOnly={isViewer}
                  onChange={(e) => onUpdate(row.id, { note: e.target.value })}
                  placeholder={isViewer ? '' : 'Add a note for trustees…'}
                />
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

/** Inline-editable forecast cell (Y1/Y2/Y3). Shows the projected value
 *  when no override is set; shows the override (in italic blue) when
 *  set. Typing a value sets the override; clearing the input commits
 *  null and reverts to the calculated forecast. */
function ForecastInput({
  displayedValue,
  override,
  onCommit,
  readOnly,
  readOnlyTitle,
}: {
  displayedValue: number;
  override: number | null;
  onCommit: (n: number | null) => void;
  /** When true, the cell renders as non-editable and shows a tooltip
   *  explaining who owns the value. Used for Quota-calculator-driven
   *  rows (Y1 of GL 210). */
  readOnly?: boolean;
  readOnlyTitle?: string;
}) {
  const [focused, setFocused] = useState(false);
  const [draft, setDraft] = useState('');
  const isOverridden = override !== null;
  const display = focused ? draft : fmt(displayedValue);

  if (readOnly) {
    // Show the projected value — which now resolves via
    //   override → quotaY* → CPI cascade
    // in projection.ts. For the bound row that means quotaY1 wins,
    // and edits to Proj/currentAmount no longer cascade in.
    return (
      <span className="num forecast-locked" title={readOnlyTitle || 'Read-only — calculated value'}>
        {fmt(displayedValue)}
      </span>
    );
  }

  const commit = () => {
    const trimmed = draft.trim();
    if (trimmed === '') {
      onCommit(null);
      return;
    }
    const cleaned = trimmed.replace(/[\s,]/g, '').replace(/^\((.+)\)$/, '-$1');
    const n = parseFloat(cleaned);
    onCommit(Number.isNaN(n) ? null : Math.round(n));
  };

  return (
    <input
      type="text"
      inputMode="numeric"
      className={`input-amount num ${isOverridden ? 'forecast-override' : ''}`}
      value={display}
      onFocus={(e) => {
        // Show whatever's stored: the override if set, otherwise the
        // calculated value (so the user can edit from a sensible base).
        setDraft(String(override ?? Math.round(displayedValue)));
        setFocused(true);
        e.target.select();
      }}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={() => {
        commit();
        setFocused(false);
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          (e.target as HTMLInputElement).blur();
        } else if (e.key === 'Escape') {
          setDraft('');
          (e.target as HTMLInputElement).blur();
        }
      }}
      title={
        isOverridden
          ? 'Manual override — clear the input to revert to the projected forecast'
          : 'Projected from CPI — type a value to override'
      }
      aria-label="Forecast amount"
    />
  );
}

/** Inline-editable amount cell. Renders the same formatted output as the
 *  Y1/Y2/Y3 columns (commas, parens for negatives) when idle; switches
 *  to the raw number for typing on focus. Enter or blur commits. */
function CurrentAmountInput({
  value,
  readOnly,
  onCommit,
}: {
  value: number;
  readOnly?: boolean;
  onCommit: (n: number) => void;
}) {
  const [focused, setFocused] = useState(false);
  const [draft, setDraft] = useState(String(value));

  const display = focused ? draft : fmt(value);

  const commit = () => {
    const cleaned = draft.replace(/[\s,]/g, '').replace(/^\((.+)\)$/, '-$1');
    const n = parseFloat(cleaned);
    onCommit(Number.isNaN(n) ? 0 : Math.round(n));
  };

  if (readOnly) {
    return <span className="num">{fmt(value)}</span>;
  }

  return (
    <input
      type="text"
      inputMode="numeric"
      className="input-amount num"
      value={display}
      onFocus={(e) => {
        setDraft(String(value));
        setFocused(true);
        e.target.select();
      }}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={() => {
        commit();
        setFocused(false);
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          (e.target as HTMLInputElement).blur();
        } else if (e.key === 'Escape') {
          setDraft(String(value));
          (e.target as HTMLInputElement).blur();
        }
      }}
      aria-label="Current-year amount"
    />
  );
}
